import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState, JobIdentity, ETDDatePicker } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Plus, Phone, AlertTriangle, Loader2 } from "lucide-react";

export default function PartPesan() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/jobs?status=all"), api.get("/orders/overdue")])
      .then(([j, o]) => { setJobs(j.data); setOverdue(o.data); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <PageHeader title="Part Pesan / Procurement" subtitle="Order otomatis dari SPK. Lengkapi Supplier, No HP & ETD (bisa sekaligus banyak item)." />
      {overdue.length > 0 && (
        <Card className="p-4 mb-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm mb-2"><AlertTriangle size={16} />OVERDUE ORDERS ({overdue.length})</div>
          {overdue.slice(0, 5).map((o) => (
            <div key={o.id} className="text-sm text-gray-700 flex flex-wrap gap-x-3"><b className="font-mono-data">{o.nopol}</b>{o.part_number}<span className="text-red-600 font-semibold">OVERDUE {o.days_overdue}D</span></div>
          ))}
        </Card>
      )}
      {loading ? <Spinner /> : jobs.length === 0 ? <EmptyState title="Belum ada job" icon={ShoppingCart} /> : (
        <div className="grid gap-3">
          {jobs.map((j) => {
            const s = j.parts_summary || {};
            return (
              <Card key={j.id} className="p-4 cursor-pointer" data-testid={`pesan-job-${j.id}`} onClick={() => navigate(`/part-pesan/${j.id}`)}>
                <div className="flex items-start justify-between gap-3">
                  <JobIdentity job={j} />
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={s.procurement || "NOT_ORDERED"} />
                    {(s.partial_lines ?? 0) > 0 && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-[rgba(255,180,0,0.5)] text-[#FFD65C]" data-testid={`pesan-${j.id}-partialbadge`}>{s.partial_lines} PARTIAL</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-1 mt-3 text-center border-t border-gray-100 pt-3">
                  {[["Item", s.total], ["Ordered", s.ordered_lines], ["Datang", s.received_lines], ["Belum", s.remaining_lines]].map(([l, v]) => (
                    <div key={l}><div className="text-[10px] uppercase text-gray-400">{l}</div><div className="font-mono-data font-semibold text-sm" data-testid={`pesan-${j.id}-${l.toLowerCase()}`}>{v ?? 0}</div></div>
                  ))}
                </div>
                {(s.total ?? 0) > 0 && (s.ordered_lines ?? 0) < s.total && (
                  <div className="text-[11px] text-[#FFD65C] text-center mt-1.5 font-medium" data-testid={`pesan-${j.id}-partial`}>{s.ordered_lines ?? 0} / {s.total} Ordered</div>
                )}
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" className="bg-gold text-white" onClick={() => setEdit(j)} data-testid={`order-btn-${j.id}`}><Plus size={14} className="mr-1" />Set Supplier / ETD</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/part-pesan/${j.id}`)}>Detail</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {edit && <OrderEditDialog job={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function OrderEditDialog({ job, onClose, onDone }) {
  const [orders, setOrders] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [sel, setSel] = useState({});
  const [form, setForm] = useState({ supplier_id: "", supplier_name: "", supplier_phone: "", etd: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [addSup, setAddSup] = useState(false);
  const [newSup, setNewSup] = useState({ name: "", phone: "", pic: "" });

  useEffect(() => {
    api.get(`/jobs/${job.id}/orders`).then((r) => { setOrders(r.data); const s = {}; r.data.forEach((o) => s[o.id] = true); setSel(s); });
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, [job.id]);

  const pick = (id) => { const su = suppliers.find((x) => x.id === id); setForm({ ...form, supplier_id: id, supplier_name: su?.name || "", supplier_phone: su?.phone || "" }); };
  const saveSup = async () => {
    if (!newSup.name) return toast.error("Nama supplier wajib");
    const { data } = await api.post("/suppliers", newSup); const l = await api.get("/suppliers"); setSuppliers(l.data); pick(data.id); setAddSup(false); setNewSup({ name: "", phone: "", pic: "" }); toast.success("Supplier ditambahkan");
  };
  const submit = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (ids.length === 0) return toast.error("Pilih minimal 1 item");
    if (!form.supplier_name) return toast.error("Pilih supplier");
    setSaving(true);
    try { await api.post("/orders/bulk", { order_ids: ids, ...form }); toast.success("Supplier/ETD diterapkan"); onDone(); }
    catch (e) { toast.error(apiError(e, "Gagal")); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Set Supplier / ETD — {job.nopol}</DialogTitle></DialogHeader>
        {!orders ? <Spinner /> : (
          <div className="space-y-4">
            <div className="text-xs text-gray-500">PO {orders[0]?.po_number || "—"} • pilih item lalu isi supplier & ETD sekaligus</div>
            <div className="border border-gray-200 rounded-lg divide-y max-h-52 overflow-y-auto">
              {orders.map((o) => (
                <label key={o.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer">
                  <Checkbox checked={!!sel[o.id]} onCheckedChange={(c) => setSel({ ...sel, [o.id]: c })} data-testid={`order-check-${o.part_number}`} />
                  <span className="flex-1"><b className="font-mono-data">{o.part_number}</b> {o.part_name} ×{o.qty_to_order}</span>
                  {o.supplier_name && <span className="text-xs text-gray-400 flex items-center gap-1">{o.supplier_name}{o.supplier_phone && <a href={`tel:${o.supplier_phone}`} className="text-blue-600"><Phone size={11} /></a>} {o.etd}</span>}
                  <StatusBadge status={o.status === "PESAN" ? "ORDERED" : o.status} />
                </label>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1"><label className="text-xs uppercase text-gray-500">Supplier</label>
                <Select value={form.supplier_id} onValueChange={pick}><SelectTrigger className="mt-1" data-testid="order-supplier"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <Button size="sm" variant="outline" onClick={() => setAddSup(!addSup)} data-testid="add-supplier-btn"><Plus size={14} />Supplier</Button>
            </div>
            {addSup && (
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Nama" value={newSup.name} onChange={(e) => setNewSup({ ...newSup, name: e.target.value })} data-testid="new-sup-name" />
                <Input placeholder="No HP" value={newSup.phone} onChange={(e) => setNewSup({ ...newSup, phone: e.target.value })} />
                <div className="flex gap-1"><Input placeholder="PIC" value={newSup.pic} onChange={(e) => setNewSup({ ...newSup, pic: e.target.value })} /><Button size="sm" onClick={saveSup} className="bg-gray-900">OK</Button></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[11px] uppercase text-gray-500">No HP Supplier</label><Input className="h-9" value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} data-testid="order-phone" /></div>
              <div><label className="text-[11px] uppercase text-gray-500">ETD</label><ETDDatePicker value={form.etd} onChange={(v) => setForm({ ...form, etd: v })} testId="order-etd" /></div>
              <div className="col-span-2"><label className="text-[11px] uppercase text-gray-500">Keterangan</label><Input className="h-9" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Tutup</Button>
          <Button onClick={submit} disabled={saving} className="bg-gray-900" data-testid="confirm-order-btn">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}Terapkan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
